// TODO: RESET PAGE SHOULD GO TO VERIFY PAGE BEFORE GO TO SETUP 2

import { report } from "@/utils/analytics/logging";
import { authSafe, cleanEmail } from "@knowt/syncing/hooks/user/auth";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import {
    checkPasswordMatches,
    passwordHasDigits,
    passwordHasLowercase,
    passwordLengthBiggerEqualTo8,
} from "@knowt/syncing/hooks/user/userVerificationUtils";
import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import { useSearchParams } from "next/navigation";
import { useRouter } from "@bprogress/next";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useAuthSlideContextSelector } from "../AuthSlide/hook/useAuthSlideContext";
import AuthKnowtLogo from "../components/AuthKnowtLogo";
import {
    AuthLeftContainer,
    AuthLeftInnerContainer,
    AuthMainContainer,
    AuthRightContainer,
} from "../components/styled/Containers";
import { AuthForm, AuthInputWithLabel, AuthSubmitButton } from "../components/styled/Forms";
import { GapPerSection } from "../components/styled/Separator";
import passwordAnimationDark from "@/public/images/lottie/sign-up-whats-the-password-dark.json";
import passwordAnimationLight from "@/public/images/lottie/sign-up-whats-the-password-light.json";
import { useThemeContextSelector } from "@/utils/theme";

const ResetPage = ({ onReset }: { onReset: () => void }) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const { userId } = useCurrentUser();
    const router = useRouter();
    const searchParams = useSearchParams();
    const username = searchParams?.get("username") || "";

    const [authStatus, setAuthStatus] = useState<"loading" | "success" | "failure" | null>(null);
    const closeAuthSlide = useAuthSlideContextSelector(state => state.closeAuthSlide);

    const [code, setCode] = useState("");
    const [email, setEmail] = useState(username);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [goToStep2, setGoToStep2] = useState(!!username);
    const [emailVerifying, setEmailVerifying] = useState(false);
    const [submittingResetRequest, setSubmittingResetRequest] = useState(false);

    const isAuthLoading = authStatus === "loading";

    // biome-ignore lint: useExhaustiveDependencies
    useEffect(() => {
        if (goToStep2) verifyEmail(email);
    }, []);

    const verifyEmail = (emailToUse: string) => {
        if (emailVerifying) return;
        if (isAuthLoading) return;

        setEmailVerifying(true);
        const cleanedEmail = cleanEmail(emailToUse || email);
        if (cleanedEmail.length === 0) {
            toast.error("Please enter an email");
            setEmailVerifying(false);
            return;
        }
        setAuthStatus("loading");
        authSafe(a => resetPassword({ username: a }))(cleanedEmail)
            .then(() => {
                setAuthStatus(null);
                setEmailVerifying(false);
                setGoToStep2(true);
            })
            .catch(e => {
                if (e.name.includes("LimitExceededException")) {
                    toast.error("You have exceeded the limit of password reset attempts. Please try again later.");
                } else {
                    toast.error("Maybe this account uses Google, Apple, or Facebook Sign In!");
                }
                setEmailVerifying(false);
                setAuthStatus("failure");
            })
            .finally(() => {
                setTimeout(() => setAuthStatus(null), 2500);
            });
    };

    const resetPasswordSubmit = () => {
        if (submittingResetRequest) return;
        setSubmittingResetRequest(true);
        const cleanedEmail = cleanEmail(email);

        if (!passwordHasDigits(newPassword)) {
            toast.error("Password must contain at least 1 digit.");
            setSubmittingResetRequest(false);
            return;
        } else if (!passwordHasLowercase(newPassword)) {
            toast.error("Password must contain at least 1 lowercase letter.");
            setSubmittingResetRequest(false);
            return;
        } else if (!passwordLengthBiggerEqualTo8(newPassword)) {
            setSubmittingResetRequest(false);
            toast.error("Password must be at least 8 characters in length");
            return;
        } else if (!checkPasswordMatches(newPassword, confirmPassword)) {
            setSubmittingResetRequest(false);
            toast.error("Passwords do not match.");
            return;
        }
        setAuthStatus("loading");
        authSafe((a, b, c) => confirmResetPassword({ username: a, confirmationCode: b, newPassword: c }))(
            cleanedEmail,
            code.trim(),
            newPassword
        )
            .then(() => {
                setAuthStatus("success");
                toast.success("You have successfully changed your password!");
                setSubmittingResetRequest(false);
                onReset();
            })
            .catch(err => {
                setSubmittingResetRequest(false);
                if (err.name === "CodeMismatchException") {
                    setAuthStatus("failure");
                    toast.error("Code is not correct");
                    setTimeout(() => setAuthStatus(null), 1000);
                } else {
                    toast.error("Something went wrong, please try again.");
                    report(err, "forgotPasswordSubmit");
                }
            })
            .finally(() => setTimeout(() => setAuthStatus(null), 3000));
    };

    const onGoBack = () => {
        closeAuthSlide();
        if (userId) {
            router.back();
        }
    };

    const getForm = () => {
        if (goToStep2) {
            return (
                <AuthForm onSubmit={resetPasswordSubmit}>
                    <AuthInputWithLabel
                        label="Code"
                        type="text"
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        placeholder={"Enter the verification code"}
                    />
                    <GapPerSection height="4rem" />
                    <AuthInputWithLabel
                        label="Enter a new password"
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder={"Enter your new password"}
                    />
                    <GapPerSection height="4rem" />
                    <AuthInputWithLabel
                        label="Confirm new password"
                        type="password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        placeholder={"Confirm your new password"}
                    />
                    <GapPerSection height="4rem" />
                    <AuthSubmitButton authStatus={authStatus}>reset password</AuthSubmitButton>
                </AuthForm>
            );
        }

        return (
            <AuthForm onSubmit={() => verifyEmail(email)}>
                <AuthInputWithLabel
                    label="Email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={"Enter your email"}
                />
                <GapPerSection height="4rem" />
                <AuthSubmitButton authStatus={authStatus}>Send verification code</AuthSubmitButton>
            </AuthForm>
        );
    };

    return (
        <AuthMainContainer>
            <AuthLeftContainer>
                <AuthKnowtLogo onClose={closeAuthSlide} />
                <AuthLeftInnerContainer style={{ margin: "auto 0" }}>
                    <h2
                        className="heading4"
                        style={{ marginBottom: "1rem" }}
                        data-testid="auto-id-86056c65-3d8e-4b42-b5b7-039de0bf3136">
                        Change your password
                    </h2>
                    <p
                        className="body2"
                        data-testid="auto-id-ced8b907-a28d-4830-b474-c13ba9338afd">Set up a new password and confirm it below.</p>
                    <GapPerSection height="4rem" />
                    {getForm()}
                    <GapPerSection height="4rem" />
                </AuthLeftInnerContainer>
            </AuthLeftContainer>
            <AuthRightContainer
                onClose={onGoBack}
                animation={isDarkMode ? passwordAnimationDark : passwordAnimationLight}
            />
        </AuthMainContainer>
    );
};

export default ResetPage;
